import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Select,
  MenuItem,
  FormControl,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

interface Option {
  value: string;
  label: string;
}

interface PaginatedSearchSelectProps {
  label: string;
  placeholder?: string;
  onChange: (selected: string) => void;
  onLoadMore: (
    search: string,
    page: number
  ) => Promise<{
    options: Option[];
    hasMore: boolean;
    total: number;
  }>;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  pageSize?: number;
  searchDebounceMs?: number;
  initialOption?: Option | null;
}

export interface PaginatedSearchSelectRef {
  reset: () => void;
  setSeletedOption: (value: string) => void;
}

const PaginatedSearchSelect = forwardRef<
  PaginatedSearchSelectRef,
  PaginatedSearchSelectProps
>(
  (
    {
      placeholder = "Select an option",
      onChange,
      onLoadMore,
      className,
      disabled = false,
      error = false,
      pageSize = 20,
      searchDebounceMs = 300,
      initialOption = null,
    }: PaginatedSearchSelectProps,
    ref
  ) => {
    const [selectedValue, setSelectedValue] = useState<string>("");
    const [selectedLabel, setSelectedLabel] = useState<string>("");
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<Option[]>([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const scrollTimeoutRef = useRef<NodeJS.Timeout>();
    const menuListRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      reset() {
        setSelectedValue("");
        setSelectedLabel("");
        setOptions([]);
        setSearch("");
        setCurrentPage(0);
        setHasMore(true);
        setInitialLoadDone(false);
        loadingRef.current = false;
      },
      setSeletedOption(value: string) {
        setSelectedValue(value);
        // Find the label for this value
        const option = options.find((opt) => opt.value === value);
        if (option) {
          setSelectedLabel(option.label);
        }
      },
    }));

    const loadingRef = useRef(false);

    // Handle initial option when provided
    useEffect(() => {
      if (initialOption && initialOption.value) {
        setSelectedValue(initialOption.value);
        setSelectedLabel(initialOption.label);
        // Add the initial option to the options list if it's not already there
        setOptions((prev) => {
          const exists = prev.some((opt) => opt.value === initialOption.value);
          if (!exists) {
            return [initialOption, ...prev];
          }
          return prev;
        });
      }
    }, [initialOption]);

    const loadOptions = useCallback(
      async (searchQuery: string, page: number, append = false) => {
        if (loadingRef.current) {
          console.log("Already loading, skipping request");
          return;
        }

        console.log(
          `[PaginatedSelect] Loading: search="${searchQuery}", page=${page}, append=${append}`
        );
        loadingRef.current = true;
        setLoading(true);

        try {
          const result = await onLoadMore(searchQuery, page);

          // Only update state if component is still mounted and this is the latest request
          if (append) {
            setOptions((prev) => {
              // Prevent duplicates when appending
              const newOptions = result.options.filter(
                (newOpt) =>
                  !prev.some((prevOpt) => prevOpt.value === newOpt.value)
              );
              return [...prev, ...newOptions];
            });
          } else {
            // When replacing options, preserve the initialOption if it exists and is not in the new results
            setOptions((prev) => {
              const hasInitialOption =
                initialOption &&
                prev.some((opt) => opt.value === initialOption.value);
              const initialInResults =
                initialOption &&
                result.options.some((opt) => opt.value === initialOption.value);

              if (hasInitialOption && !initialInResults) {
                // Keep initial option at the top if it's not in the results
                return [initialOption, ...result.options];
              }
              return result.options;
            });
          }

          setHasMore(result.hasMore);
          setTotal(result.total);
          setCurrentPage(page);

          console.log(
            `[PaginatedSelect] Loaded: ${result.options.length} options, hasMore: ${result.hasMore}`
          );
        } catch (error) {
          console.error("Error loading options:", error);
          if (!append) {
            setOptions([]);
            setHasMore(false);
            setTotal(0);
          }
        } finally {
          loadingRef.current = false;
          setLoading(false);
        }
      },
      [onLoadMore, initialOption]
    );

    // Initial load when dropdown opens
    useEffect(() => {
      if (isOpen && !initialLoadDone) {
        setInitialLoadDone(true);
        loadOptions("", 0);
      }
    }, [isOpen, initialLoadDone, loadOptions]);

    // Debounced search
    useEffect(() => {
      if (!isOpen || !initialLoadDone) return;

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        setCurrentPage(0);
        loadOptions(search, 0, false);
      }, searchDebounceMs);

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [search, isOpen, initialLoadDone, loadOptions, searchDebounceMs]);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

        // Clear existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Debounce scroll events
        scrollTimeoutRef.current = setTimeout(() => {
          const scrolledToBottom =
            scrollHeight - scrollTop <= clientHeight + 10; // Reduced threshold

          // Only log when actually loading more data
          if (scrolledToBottom && hasMore && !loadingRef.current) {
            console.log(
              `[PaginatedSelect] Scroll reached bottom: loading page ${
                currentPage + 1
              }`
            );
          }

          // Load more when scrolled to bottom
          if (scrolledToBottom && hasMore && !loadingRef.current) {
            console.log(
              `[PaginatedSelect] Loading more on scroll, page: ${currentPage} -> ${
                currentPage + 1
              }`
            );
            loadOptions(search, currentPage + 1, true);
          }
        }, 100); // 100ms debounce
      },
      [hasMore, search, currentPage, loadOptions]
    );

    const handleChange = (value: string) => {
      const option = options.find((opt) => opt.value === value);
      setSelectedValue(value);
      setSelectedLabel(option?.label || "");
      setIsOpen(false);
      onChange(value);
    };

    const handleOpen = () => {
      setIsOpen(true);
    };

    const handleClose = () => {
      setIsOpen(false);
      setSearch(""); // Reset search when closing
      setInitialLoadDone(false); // Allow reload on next open
      loadingRef.current = false; // Reset loading state
    };

    // Determine border color based on error state
    const getBorderColor = () => {
      if (error) return "#ff0000";
      if (disabled) return "#E5E7EB";
      return "#CBD2D9";
    };

    const getBorderWidth = () => {
      if (error) return "1px";
      return "1px";
    };

    return (
      <FormControl
        error={error}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            background: "#fff",
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: "3px",
            width: "100%",
            border: `${getBorderWidth()} solid ${getBorderColor()}`,
            transition: "all 0.2s ease",
            minHeight: "40px",
            height: "40px",
            boxSizing: "border-box",
            "&:hover": {
              borderColor: error ? "#ff0000" : "#1062b9",
              borderWidth: error ? "1px" : "1.5px",
            },
            "&.Mui-focused": {
              borderColor: error ? "#ff0000" : "#1062b9",
              borderWidth: error ? "1px" : "1.5px",
              boxShadow: error
                ? "0 0 0 2px rgba(255, 0, 0, 0.12)"
                : "0 0 0 2px rgba(16, 98, 185, 0.12)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#F5F7F7",
              borderColor: "#E5E7EB",
            },
            "& fieldset": {
              border: "none",
            },
          },
          "& .MuiSelect-select": {
            padding: "10px 14px",
            fontSize: "15px",
            fontWeight: 500,
            color: "#59585B",
            minHeight: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            "&.Mui-disabled": {
              color: "#838285",
            },
            "&.MuiSelect-displayEmpty": {
              color: "#cbd2d9",
            },
          },
          "& .MuiSelect-icon": {
            color: error ? "#ff0000" : "#59585B",
            right: "12px",
          },
        }}
      >
        <Select
          disabled={disabled}
          value={selectedValue}
          onChange={(e) => handleChange(e.target.value as string)}
          className={className}
          displayEmpty
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#cbd2d9", fontWeight: 500 }}>
                  {placeholder}
                </span>
              );
            }
            return selectedLabel || selected;
          }}
          MenuProps={{
            PaperProps: {
              style: {
                borderRadius: "8px",
                border: "1px solid #CBD2D9",
                boxShadow: "0 8px 32px 0 rgba(16, 98, 185, 0.10)",
                marginTop: "4px",
                maxHeight: 400,
                padding: 0,
              },
            },
          }}
        >
          {/* Search input at the top */}
          <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #F3F4F6" }}>
            <TextField
              size="small"
              placeholder="Search trucks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "#CBD2D9", mr: 1 }} />,
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover fieldset": {
                    borderColor: "#1062b9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1062b9",
                  },
                },
              }}
            />
          </Box>

          {/* Options list with scroll */}
          <Box
            ref={menuListRef}
            onScroll={handleScroll}
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD2D9",
                borderRadius: "3px",
                "&:hover": {
                  background: "#9CA3AF",
                },
              },
            }}
          >
            {options.length === 0 && !loading ? (
              <MenuItem disabled>
                <Typography color="textSecondary">
                  {search ? "No trucks found" : "No trucks available"}
                </Typography>
              </MenuItem>
            ) : (
              options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  onClick={() => handleChange(option.value)}
                  sx={{
                    padding: "12px 18px",
                    fontSize: "15px",
                    fontWeight: 500,
                    color: "#59585B",
                    minHeight: "44px",
                    maxHeight: "44px",
                    borderBottom: "1px solid #F3F4F6",
                    "&:last-child": {
                      borderBottom: "none",
                    },
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#E3F2FD",
                      color: "#1062b9",
                      "&:hover": {
                        backgroundColor: "#E3F2FD",
                      },
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))
            )}

            {/* Loading indicator */}
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 2,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1, color: "#666" }}>
                  Loading trucks...
                </Typography>
              </Box>
            )}

            {/* Load more indicator */}
            {!loading && hasMore && options.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 1,
                  borderTop: "1px solid #F3F4F6",
                  backgroundColor: "#F8F9FA",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#1062b9", fontWeight: 500 }}
                >
                  📥 Scroll down to load more trucks...
                </Typography>
              </Box>
            )}

            {/* No more data indicator */}
            {!loading &&
              !hasMore &&
              options.length > 0 &&
              total > options.length && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 1,
                    borderTop: "1px solid #F3F4F6",
                    backgroundColor: "#F8F9FA",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#10B981", fontWeight: 500 }}
                  >
                    ✅ All trucks loaded
                  </Typography>
                </Box>
              )}

            {/* Total count */}
            {options.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 1,
                  borderTop: "1px solid #F3F4F6",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {options.length > 0 &&
                    (hasMore
                      ? `Loaded ${options.length} trucks (scroll for more)`
                      : total > 0
                      ? `Showing all ${total} trucks`
                      : `Loaded ${options.length} trucks (all available)`)}
                </Typography>
              </Box>
            )}
          </Box>
        </Select>
      </FormControl>
    );
  }
);

PaginatedSearchSelect.displayName = "PaginatedSearchSelect";

export default PaginatedSearchSelect;
